import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users, Briefcase, FileText, Wrench, Handshake, ChevronRight } from "lucide-react";
import { Spinner } from "./ui/kit";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canSee, visibleNav } from "../lib/nav";

/**
 * Hledání napříč aplikací: klienti, obchodní případy, nabídky, zařízení, partneři.
 * Na telefonu přes celou obrazovku (otevře ho pole „Hledat" v horní liště),
 * na počítači okno uprostřed (tlačítko v levém panelu nebo ⌘K / Ctrl+K).
 * Hledá jen v modulech, které uživatel smí vidět.
 */
const ZDROJE = [
  { key: "klienti", titul: "Klienti", ikona: Users, url: "/api/clients", params: { limit: 6 },
    radek: (c) => ({ nazev: c.display_name, pod: [c.telefon, c.adresa?.mesto].filter(Boolean).join(" · "), cesta: `/klienti/${c.id}` }) },
  { key: "pripady", titul: "Obchodní případy", ikona: Briefcase, url: "/api/obchodni-pripady", params: { limit: 6 },
    radek: (p) => ({ nazev: p.nazev, pod: [p.cislo, p.client_name].filter(Boolean).join(" · "), cesta: `/obchod/${p.id}` }) },
  { key: "nabidky", titul: "Nabídky", ikona: FileText, url: "/api/nabidky", params: {},
    radek: (n) => ({ nazev: n.nazev, pod: [n.cislo, n.klient].filter(Boolean).join(" · "), cesta: `/nabidky/${n.id}` }) },
  { key: "servis", titul: "Zařízení", ikona: Wrench, url: "/api/zarizeni", params: {},
    radek: (z) => ({ nazev: z.nazev, pod: [z.vyrobni_cislo, z.client_name].filter(Boolean).join(" · "), cesta: `/servis/zarizeni/${z.id}` }) },
  { key: "partneri", titul: "Obchodní partneři", ikona: Handshake, url: "/api/partneri", params: {},
    radek: (p) => ({ nazev: p.nazev, pod: p.ico ? `IČO ${p.ico}` : "", cesta: `/partneri/${p.id}` }) },
];

function viditelneKlice(user) {
  const klice = new Set();
  visibleNav(user, canSee).forEach((i) => { klice.add(i.key); (i.children || []).forEach((c) => klice.add(c.key)); });
  return klice;
}

export default function Hledani({ onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [vysledky, setVysledky] = useState(null);   // null = nic nehledám
  const [hledam, setHledam] = useState(false);
  const vstup = useRef(null);
  const poradi = useRef(0);

  // iPhone: s otevřenou klávesnicí prohlížeč posune celou stránku nahoru a pole
  // hledání uteče nad okraj obrazovky. Okno proto drží výšku a polohu viditelné
  // části (visualViewport) — pole zůstane nahoře, výsledky nad klávesnicí.
  const [viditelne, setViditelne] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || window.innerWidth >= 768) return undefined;
    const zmer = () => setViditelne({ top: vv.offsetTop, height: vv.height });
    zmer();
    vv.addEventListener("resize", zmer);
    vv.addEventListener("scroll", zmer);
    return () => { vv.removeEventListener("resize", zmer); vv.removeEventListener("scroll", zmer); };
  }, []);

  useEffect(() => {
    vstup.current?.focus({ preventScroll: true });
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    const puvodni = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = puvodni; };
  }, [onClose]);

  useEffect(() => {
    const dotaz = q.trim();
    if (dotaz.length < 2) { setVysledky(null); setHledam(false); return undefined; }
    const moje = ++poradi.current;
    setHledam(true);
    const t = setTimeout(async () => {
      const klice = viditelneKlice(user);
      const zdroje = ZDROJE.filter((z) => klice.has(z.key));
      const odpovedi = await Promise.allSettled(zdroje.map((z) => api.get(z.url, { params: { ...z.params, q: dotaz } })));
      if (moje !== poradi.current) return;   // mezitím se psalo dál
      setVysledky(zdroje.map((z, i) => {
        const r = odpovedi[i];
        const items = r.status === "fulfilled" ? (r.value.data?.items || r.value.data || []) : [];
        return { ...z, radky: (Array.isArray(items) ? items : []).slice(0, 6).map(z.radek) };
      }).filter((s) => s.radky.length));
      setHledam(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, user]);

  const otevrit = (cesta) => { onClose(); nav(cesta); };

  return (
    <div className="fixed inset-0 z-[60] md:flex md:items-start md:justify-center md:pt-[12vh]" data-testid="hledani" role="dialog" aria-modal="true" aria-label="Hledat"
      style={viditelne ? { top: viditelne.top, height: viditelne.height, bottom: "auto" } : undefined}>
      <div className="hidden md:block absolute inset-0 bg-black/30 zaclon-in" onClick={onClose} />
      <div className="relative h-full md:h-auto md:max-h-[70vh] w-full md:max-w-[560px] bg-surface md:rounded-[24px] md:shadow-panel flex flex-col okno-in">
        <div className="px-3.5 pt-[calc(env(safe-area-inset-top)+8px)] md:pt-3.5 pb-2 flex items-center gap-2.5">
          <label className="flex-1 min-w-0 h-11 rounded-full bg-white shadow-[0_0_0_1px_#eeece7] flex items-center gap-2.5 px-4 text-sec">
            {hledam ? <Spinner /> : <Search size={18} strokeWidth={2.2} className="shrink-0" />}
            <input ref={vstup} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Klient, případ, nabídka, zařízení…"
              className="flex-1 min-w-0 bg-transparent outline-none text-[16px] md:text-[14px] text-ink placeholder:text-sec" data-testid="hledani-vstup"
              enterKeyHint="search" />
          </label>
          <button type="button" onClick={onClose} aria-label="Zavřít hledání" data-testid="hledani-zavrit"
            className="tap h-11 w-11 rounded-full bg-white shadow-[0_0_0_1px_#eeece7] text-ink inline-flex items-center justify-center shrink-0">
            <X size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3.5 pb-[calc(env(safe-area-inset-bottom)+20px)] md:pb-4">
          {vysledky === null && (
            <p className="px-2 pt-6 t-telo text-muted text-center">Napiš aspoň dvě písmena — jméno, číslo případu, telefon nebo výrobní číslo.</p>
          )}
          {vysledky && !vysledky.length && !hledam && (
            <p className="px-2 pt-6 t-telo text-muted text-center" data-testid="hledani-nic">Nic jsme nenašli pro „{q.trim()}“.</p>
          )}
          {(vysledky || []).map((s) => {
            const Ikona = s.ikona;
            return (
              <div key={s.key} data-testid={`hledani-${s.key}`}>
                <div className="label-up text-muted px-1.5 pt-4 pb-[7px]">{s.titul}</div>
                <div className="bg-white rounded-[20px] overflow-hidden">
                  {s.radky.map((r, j) => (
                    <button key={r.cesta} type="button" onClick={() => otevrit(r.cesta)}
                      className={`w-full min-h-[56px] px-3.5 py-2 flex items-center gap-3 text-left hover:bg-inputbg active:bg-inputbg ${j ? "border-t border-line-soft" : ""}`}>
                      <span className="h-[34px] w-[34px] rounded-[11px] bg-surface text-sec inline-flex items-center justify-center shrink-0"><Ikona size={17} strokeWidth={1.9} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block t-telo font-600 text-ink truncate">{r.nazev}</span>
                        {r.pod && <span className="block t-meta text-muted truncate">{r.pod}</span>}
                      </span>
                      <ChevronRight size={16} className="text-[#c4c2bd] shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
